import { QueryClient } from '@tanstack/react-query';
import { ApiClientError } from './api';

/**
 * Configure the TanStack Query client with sensible defaults
 * for the Mission Control dashboard
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: data considered fresh for 30 seconds
      staleTime: 30 * 1000,
      // Cache time: keep unused data in cache for 5 minutes
      gcTime: 5 * 60 * 1000,
      // Retry configuration
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error instanceof ApiClientError && error.status >= 400 && error.status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus for fresh data
      refetchOnWindowFocus: true,
      // Don't refetch on reconnect by default
      refetchOnReconnect: false,
    },
    mutations: {
      // Don't retry mutations by default
      retry: false,
    },
  },
});

/**
 * Query key factory for consistent cache key management
 */
export const queryKeys = {
  // Dashboard
  dashboard: ['dashboard'] as const,
  dashboardStats: () => [...queryKeys.dashboard, 'stats'] as const,

  // Projects
  projects: ['projects'] as const,
  projectList: (params?: { page?: number; status?: string }) =>
    [...queryKeys.projects, 'list', params] as const,
  projectDetail: (id: string) => [...queryKeys.projects, 'detail', id] as const,
  projectWorkflows: (id: string) => [...queryKeys.projects, 'workflows', id] as const,

  // Workflows
  workflows: ['workflows'] as const,
  workflowList: (params?: { page?: number; projectId?: string; status?: string }) =>
    [...queryKeys.workflows, 'list', params] as const,
  workflowDetail: (id: string) => [...queryKeys.workflows, 'detail', id] as const,

  // Settings
  settings: ['settings'] as const,
} as const;

/**
 * Invalidate all project-related queries
 */
export function invalidateProjectQueries() {
  return queryClient.invalidateQueries({ queryKey: queryKeys.projects });
}

/**
 * Invalidate all workflow-related queries
 */
export function invalidateWorkflowQueries() {
  return queryClient.invalidateQueries({ queryKey: queryKeys.workflows });
}

/**
 * Invalidate dashboard queries
 */
export function invalidateDashboardQueries() {
  return queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
}
